import React from 'react';
import { Form } from 'react-bootstrap';

import { CreateUpdateParamsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import { useTranslation } from '../../../common/hooks/useTranslation';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  ParamsGroupObj,
  ParamsNameObj,
} from '../../../common/pokCore/validation/enums';

interface ParamsFormProps {
  id?: string;
  param: CreateUpdateParamsDto;
  readOnly: boolean;
  propertyChange: (obj: Partial<CreateUpdateParamsDto>) => void;
  createErrors: (errors: string[]) => void;
}

export const ParamsForm: React.FC<ParamsFormProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const pok = usePokCore();

  const handleValueChange = (value: string) => {
    const update: Partial<CreateUpdateParamsDto> = {
      valueString: undefined,
      valueBoolean: undefined,
      valueNumber: undefined,
      valueDate: undefined,
    };

    switch (props.param.type) {
      case 'String':
        update.valueString = value;
        break;
      case 'Boolean':
        update.valueBoolean = value === 'true';
        break;
      case 'Number':
        update.valueNumber = value ? parseFloat(value) : undefined;
        break;
      case 'Date':
        if (value) {
          const localDate = new Date(value);
          update.valueDate = localDate;
        } else {
          update.valueDate = undefined;
        }
        break;
    }

    props.propertyChange(update);
  };

  const getCurrentValue = () => {
    switch (props.param.type) {
      case 'String':
        return props.param.valueString || '';
      case 'Boolean':
        return props.param.valueBoolean?.toString() || 'false';
      case 'Number':
        return props.param.valueNumber?.toString() || '';
      case 'Date':
        if (props.param.valueDate) {
          const date = new Date(props.param.valueDate);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        }
        return '';
      default:
        return '';
    }
  };

  const renderValueInput = () => {
    if (!props.param.isEditable) {
      let displayValue = getCurrentValue();

      if (props.param.type === 'Date' && props.param.valueDate) {
        displayValue = new Date(props.param.valueDate).toLocaleString();
      }

      return (
        <Form.Control type="text" value={displayValue} readOnly disabled />
      );
    }

    switch (props.param.type) {
      case 'String':
        return (
          <Form.Control
            type="text"
            value={getCurrentValue()}
            onChange={e => handleValueChange(e.target.value)}
            readOnly={props.readOnly}
          />
        );
      case 'Boolean':
        return (
          <Form.Select
            value={getCurrentValue()}
            onChange={e => handleValueChange(e.target.value)}
            disabled={props.readOnly}
          >
            <option value="true">{t(tk.common.yes)}</option>
            <option value="false">{t(tk.common.no)}</option>
          </Form.Select>
        );
      case 'Number':
        return (
          <Form.Control
            type="number"
            step="any"
            value={getCurrentValue()}
            onChange={e => handleValueChange(e.target.value)}
            readOnly={props.readOnly}
          />
        );
      case 'Date':
        return (
          <Form.Control
            type="datetime-local"
            value={getCurrentValue()}
            onChange={e => handleValueChange(e.target.value)}
            readOnly={props.readOnly}
            title="Wybierz datę i godzinę"
            step="60"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Form className="d-grid gap-3">
      <FormRow controlId="group" label={t(tk.administration.group)}>
        <Form.Control
          type="text"
          value={t(ParamsGroupObj[props.param.group])}
          readOnly
          disabled
        />
      </FormRow>

      <FormRow controlId="name" label={t(tk.common.name)}>
        <Form.Control
          type="text"
          value={t(ParamsNameObj[props.param.name])}
          readOnly
          disabled
        />
      </FormRow>

      <FormRow controlId="value" label={t(tk.administration.value)}>
        {renderValueInput()}
      </FormRow>

      {props.id && (
        <LastEditorInfo
          className="mt-3 color-primary"
          id={props.id}
          method={pok.params.getLastEditor}
        />
      )}
    </Form>
  );
};
